import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Drives the admin Push Notification Broadcast UI.
 *
 * - Form state: audience, title, body, deep_link, specific user_ids, scheduled_for.
 * - Live debounced `audience-size` preview so admins see recipient counts
 *   before sending.
 * - `sendOrSchedule()` hits POST /api/notifications/broadcasts. When
 *   `scheduledFor` is empty, the backend sends immediately and returns a
 *   delivery report. When `scheduledFor` is a future ISO datetime, the
 *   backend persists the broadcast and the scheduler will dispatch it.
 *
 * @param {object} opts
 * @param {boolean} opts.enabled  - gate auto-fetches (e.g. tab visibility)
 * @param {() => void} [opts.onBroadcastSaved] - called after a successful
 *   create so a parent BroadcastHistoryCard can refresh its list.
 */
export function useAdminPushBroadcast({ enabled, onBroadcastSaved }) {
  const [audience, setAudience] = useState('self');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [deepLink, setDeepLink] = useState('');
  const [specificUserIds, setSpecificUserIds] = useState('');
  // ISO local datetime (`<input type="datetime-local">` format). Empty
  // string means "send now".
  const [scheduledFor, setScheduledFor] = useState('');
  // Recurrence: 'none' | 'daily' | 'weekly'
  const [recurrenceCadence, setRecurrenceCadence] = useState('none');
  const [recurrenceTime, setRecurrenceTime] = useState('09:00');
  const [recurrenceDays, setRecurrenceDays] = useState([]); // 0=Mon..6=Sun

  const toggleRecurrenceDay = useCallback((d) => {
    setRecurrenceDays((prev) =>
      prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d].sort()
    );
  }, []);

  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [lastReport, setLastReport] = useState(null);

  const previewTimer = useRef(null);

  const parseUserIds = useCallback(
    () =>
      specificUserIds
        .split(/[\s,]+/)
        .map((s) => s.trim())
        .filter(Boolean),
    [specificUserIds]
  );

  // Convert the datetime-local string ("2026-06-19T20:00") to an ISO
  // string with timezone. Empty → null (immediate send).
  const scheduledForISO = useCallback(() => {
    if (!scheduledFor) return null;
    const d = new Date(scheduledFor);
    if (Number.isNaN(d.getTime())) return null;
    return d.toISOString();
  }, [scheduledFor]);

  // --- audience preview (debounced) ---
  useEffect(() => {
    if (!enabled) return;
    if (previewTimer.current) clearTimeout(previewTimer.current);

    previewTimer.current = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const res = await axios.post(
          `${API}/notifications/audience-size`,
          { audience, user_ids: parseUserIds() },
          { withCredentials: true }
        );
        setPreview(res.data);
      } catch (err) {
        setPreview(null);
        if (err.response?.status && err.response.status !== 403) {
          console.error('audience-size failed', err);
        }
      } finally {
        setPreviewLoading(false);
      }
    }, 300);

    return () => {
      if (previewTimer.current) clearTimeout(previewTimer.current);
    };
  }, [audience, specificUserIds, enabled, parseUserIds]);

  const resetForm = useCallback(() => {
    setTitle('');
    setBody('');
    setDeepLink('');
    setSpecificUserIds('');
    setScheduledFor('');
    setRecurrenceCadence('none');
    setRecurrenceDays([]);
  }, []);

  /** Populate the form from a past broadcast (Clone action in history). */
  const cloneFrom = useCallback((b) => {
    setAudience(b.audience || 'self');
    setTitle(b.title || '');
    setBody(b.body || '');
    setDeepLink(b.deep_link || '');
    setSpecificUserIds((b.user_ids || []).join(', '));
    setScheduledFor(''); // never auto-reschedule; user picks new time.
    const rec = b.recurrence;
    if (rec?.cadence) {
      setRecurrenceCadence(rec.cadence);
      setRecurrenceTime(rec.time_utc || '09:00');
      setRecurrenceDays(rec.days_of_week || []);
    } else {
      setRecurrenceCadence('none');
      setRecurrenceDays([]);
    }
  }, []);

  const sendOrSchedule = useCallback(async () => {
    if (!title.trim() || !body.trim()) {
      toast.error('Title and body are required');
      return;
    }
    if (audience === 'specific' && parseUserIds().length === 0) {
      toast.error('Add at least one user ID for "Specific users"');
      return;
    }

    const isoSched = scheduledForISO();
    const isRecurring = recurrenceCadence !== 'none';
    if (isRecurring && isoSched) {
      toast.error('Use a one-off schedule OR a recurrence, not both.');
      return;
    }
    if (isRecurring && recurrenceCadence === 'weekly' && recurrenceDays.length === 0) {
      toast.error('Pick at least one day of the week for weekly recurrence.');
      return;
    }

    const recurrencePayload = isRecurring
      ? {
          cadence: recurrenceCadence,
          time_utc: recurrenceTime,
          days_of_week: recurrenceCadence === 'weekly' ? recurrenceDays : [],
        }
      : null;

    setSending(true);
    setLastReport(null);
    try {
      const res = await axios.post(
        `${API}/notifications/broadcasts`,
        {
          audience,
          user_ids: parseUserIds(),
          title: title.trim(),
          body: body.trim(),
          deep_link: deepLink.trim() || null,
          scheduled_for: isoSched,
          recurrence: recurrencePayload,
        },
        { withCredentials: true }
      );
      const broadcast = res.data;
      setLastReport(broadcast.delivery_report || { status: broadcast.status });

      if (broadcast.status === 'recurring') {
        const nextFire = broadcast.next_fire_at
          ? new Date(broadcast.next_fire_at).toLocaleString()
          : 'unknown';
        toast.success(`Recurring broadcast saved — next fire ${nextFire}`);
        resetForm();
      } else if (broadcast.status === 'scheduled') {
        toast.success(`Scheduled for ${new Date(broadcast.scheduled_for).toLocaleString()}`);
        resetForm();
      } else {
        const report = broadcast.delivery_report || {};
        const { sent = 0, failed = 0, audience_size = 0 } = report;
        if (audience_size === 0) {
          toast.warning('No recipients in that audience');
        } else if (sent > 0) {
          toast.success(`Push sent to ${sent}/${audience_size}${failed ? ` (${failed} failed)` : ''}`);
        } else {
          toast.error(`All ${audience_size} sends failed`);
        }
      }

      onBroadcastSaved?.();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      setLastReport({ error: detail });
      if (err.response?.status === 503) {
        toast.error('Firebase not configured — see /app/frontend/FIREBASE_SETUP.md');
      } else {
        toast.error(`Push failed: ${detail}`);
      }
    } finally {
      setSending(false);
    }
  }, [
    audience,
    title,
    body,
    deepLink,
    parseUserIds,
    scheduledForISO,
    recurrenceCadence,
    recurrenceTime,
    recurrenceDays,
    resetForm,
    onBroadcastSaved,
  ]);

  return {
    audience,
    setAudience,
    title,
    setTitle,
    body,
    setBody,
    deepLink,
    setDeepLink,
    specificUserIds,
    setSpecificUserIds,
    scheduledFor,
    setScheduledFor,
    recurrenceCadence,
    setRecurrenceCadence,
    recurrenceTime,
    setRecurrenceTime,
    recurrenceDays,
    toggleRecurrenceDay,
    preview,
    previewLoading,
    sending,
    lastReport,
    sendOrSchedule,
    cloneFrom,
    resetForm,
  };
}
